import API from './api';

// --- Interfaces for Admin API ---
export interface Scholar {
  id: string;
  religion: string;
  status?: string;
  onboardingStep?: string;
  user: {
    name: string;
    phone: string;
    email?: string;
  };
  totalReviews?: number;
  rating?: number;
  createdAt: string;
}

export interface Booking {
  id: string;
  status: string;
  scheduledAt: string;
  durationMin: number;
  price: number;
  customer: {
    user: {
      name: string;
      phone?: string;
      email?: string;
    };
  };
  scholar: {
    user: {
      name: string;
    };
  };
  service: {
    name: string;
    religion?: string;
  };
  payment?: any;
}

export interface ServiceData {
  id: string;
  name: string;
  description: string;
  religion: string;
  basePrice: number;
  startRange?: number;
  endRange?: number;
  durationMin: number;
  platformFix?: number;
  platformPercentage?: number;
  isActive: boolean;
  isCustom: boolean;
  isApproved: boolean;
  iconUrl?: string;
}

export interface AdminListItem {
  id: string;
  name: string;
  email: string;
  role: 'ADMIN' | 'SUPER_ADMIN' | string;
  religion?: string;
  createdAt: string;
  managerId?: string | null;
  department?: string | null;
}

export interface HierarchyNode {
  adminId: string;
  name: string;
  role: string;
  children?: HierarchyNode[];
}

export interface AdminPerformanceItem {
  adminId: string;
  name: string;
  role: string;
  ticketsSolved: number;
  disputesSolved: number;
  avgTicketResolutionHours?: number;
  avgDisputeResolutionHours?: number;
}

export interface AnalyticsData {
  totalUsers: number;
  totalScholars: number;
  totalBookings: number;
  totalRevenue: number;
  periodCurrent?: {
    users: number;
    scholars: number;
    bookings: number;
    revenue: number;
  };
  trends?: {
    users: number;
    scholars: number;
    bookings: number;
    revenue: number;
  };
  previousPeriod?: {
    totalUsers: number;
    totalScholars: number;
    totalBookings: number;
    totalRevenue: number;
  };
  operationalMetrics?: {
    avgOrderValue: number;
    scholarUtilization: number;
    cancellationRate: number;
    repeatBookingRate: number;
    completedBookings: number;
    cancelledBookings: number;
    revenueByReligion: { religion: string; amount: number }[];
  };
}

// ═══════════════════════════════════════════════════════════
// ANALYTICS
// ═══════════════════════════════════════════════════════════

export const getAnalytics = async (period = '30d', department?: string): Promise<any> => {
  try {
    const deptParam = department ? `&department=${department}` : '';
    const response = await API.get(`/admin/analytics?period=${period}${deptParam}`);
    if (response.data.success) return response.data.data;
    return null;
  } catch (error) {
    console.error('Error fetching analytics:', error);
    return null;
  }
};

export const getAnalyticsChartData = async (months = 6): Promise<any[]> => {
  try {
    const response = await API.get(`/admin/revenue-chart?months=${months}`);
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) {
    console.error('Error fetching analytics chart data:', error);
    return [];
  }
};

export const getReligionDistribution = async (period = 'all'): Promise<any> => {
  try {
    const response = await API.get(`/admin/analytics/religion-distribution?period=${period}`);
    if (response.data.success) return response.data.data;
    return null;
  } catch (error) {
    console.error('Error fetching religion distribution:', error);
    return null;
  }
};

export const getTopScholars = async (limit = 5): Promise<any[]> => {
  try {
    const response = await API.get(`/admin/analytics/top-scholars?limit=${limit}`);
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) {
    console.error('Error fetching top scholars:', error);
    return [];
  }
};

export const getTopLocations = async (limit = 5): Promise<any[]> => {
  try {
    const response = await API.get(`/admin/analytics/top-locations?limit=${limit}`);
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) {
    console.error('Error fetching top locations:', error);
    return [];
  }
};

export const getRetentionData = async (): Promise<any> => {
  try {
    const response = await API.get('/admin/analytics/retention');
    if (response.data.success) return response.data.data;
    return null;
  } catch (error) {
    console.error('Error fetching retention data:', error);
    return null;
  }
};

export const getConversionFunnel = async (): Promise<any> => {
  try {
    const response = await API.get('/admin/analytics/conversion-funnel');
    if (response.data.success) return response.data.data;
    return null;
  } catch (error) {
    console.error('Error fetching conversion funnel:', error);
    return null;
  }
};

// ═══════════════════════════════════════════════════════════
// SCHOLARS
// ═══════════════════════════════════════════════════════════

export interface ScholarListResult {
  items: Scholar[];
  summary: { total: number; pending: number; approved: number; rejected: number };
}

export const getScholars = async (
  filters: { search?: string; religion?: string; status?: string } = {}
): Promise<ScholarListResult> => {
  try {
    const params = new URLSearchParams();
    if (filters.search)   params.set('search',   filters.search);
    if (filters.religion) params.set('religion', filters.religion);
    if (filters.status)   params.set('status',   filters.status);
    const qs = params.toString();
    const response = await API.get(`/admin/scholars${qs ? `?${qs}` : ''}`);
    if (response.data.success) return response.data.data;
    return { items: [], summary: { total: 0, pending: 0, approved: 0, rejected: 0 } };
  } catch (error) {
    console.error('Error fetching scholars:', error);
    return { items: [], summary: { total: 0, pending: 0, approved: 0, rejected: 0 } };
  }
};

export const getPendingScholars = async (): Promise<Scholar[]> => {
  try {
    const response = await API.get('/admin/scholars/pending');
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) {
    console.error('Error fetching pending scholars:', error);
    return [];
  }
};

export const approveScholar = async (id: string) => {
  try {
    const response = await API.post(`/admin/scholars/${id}/approve`);
    return response.data;
  } catch (error) {
    console.error('Error approving scholar:', error);
    throw error;
  }
};

export const rejectScholar = async (id: string, reason?: string) => {
  try {
    const response = await API.post(`/admin/scholars/${id}/reject`, { reason });
    return response.data;
  } catch (error) {
    console.error('Error rejecting scholar:', error);
    throw error;
  }
};

export const deleteScholar = async (id: string) => {
  try {
    const response = await API.delete(`/admin/scholars/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting scholar:', error);
    throw error;
  }
};

export const getScholarScheduleDetails = async (id: string, days = 14) => {
  try {
    const response = await API.get(`/admin/scholars/${id}/schedule-details?days=${days}`);
    if (response.data.success) return response.data.data;
    return null;
  } catch (error) {
    console.error('Error fetching scholar schedule details:', error);
    return null;
  }
};

// ═══════════════════════════════════════════════════════════
// BOOKINGS
// ═══════════════════════════════════════════════════════════

export interface BookingListResult {
  items: Booking[];
  summary: { total: number; confirmed: number; pending: number; ongoing: number; cancelled: number };
}

export const getBookings = async (
  filters: { search?: string; status?: string; religion?: string } = {}
): Promise<BookingListResult> => {
  try {
    const params = new URLSearchParams();
    if (filters.search)   params.set('search',   filters.search);
    if (filters.status)   params.set('status',   filters.status);
    if (filters.religion) params.set('religion', filters.religion);
    const qs = params.toString();
    const response = await API.get(`/admin/bookings${qs ? `?${qs}` : ''}`);
    if (response.data.success) return response.data.data;
    return { items: [], summary: { total: 0, confirmed: 0, pending: 0, ongoing: 0, cancelled: 0 } };
  } catch (error) {
    console.error('Error fetching bookings:', error);
    return { items: [], summary: { total: 0, confirmed: 0, pending: 0, ongoing: 0, cancelled: 0 } };
  }
};

// ═══════════════════════════════════════════════════════════
// SERVICES
// ═══════════════════════════════════════════════════════════

export const getServices = async (): Promise<ServiceData[]> => {
  try {
    const response = await API.get('/admin/services');
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) {
    console.error('Error fetching services:', error);
    return [];
  }
};

export const getServiceStats = async (): Promise<any[]> => {
  try {
    const response = await API.get('/admin/services/stats');
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) {
    console.error('Error fetching service stats:', error);
    return [];
  }
};

export interface ServiceUpsertPayload {
  name?: string;
  description?: string;
  religion?: string;
  basePrice?: number;
  startRange?: number;
  endRange?: number;
  durationMin?: number;
  platformFix?: number;
  platformPercentage?: number;
  isActive?: boolean;
  isCustom?: boolean;
  isApproved?: boolean;
  iconUrl?: string;
}

export const createService = async (data: ServiceUpsertPayload): Promise<any> => {
  try {
    const response = await API.post('/admin/services', data);
    return response.data;
  } catch (error) {
    console.error('Error creating service:', error);
    throw error;
  }
};

export const updateService = async (id: string, data: ServiceUpsertPayload): Promise<any> => {
  try {
    const response = await API.put(`/admin/services/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating service:', error);
    throw error;
  }
};

export const deleteService = async (id: string): Promise<any> => {
  try {
    const response = await API.delete(`/admin/services/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting service:', error);
    throw error;
  }
};

// ═══════════════════════════════════════════════════════════
// USERS
// ═══════════════════════════════════════════════════════════

export interface UserListResult {
  items: any[];
  summary: { total: number; active: number; suspended: number; newToday: number };
}

export const getUsers = async (
  filters: { search?: string; status?: string } = {}
): Promise<UserListResult> => {
  try {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.status) params.set('status', filters.status);
    const qs = params.toString();
    const response = await API.get(`/admin/users${qs ? `?${qs}` : ''}`);
    if (response.data.success) return response.data.data;
    return { items: [], summary: { total: 0, active: 0, suspended: 0, newToday: 0 } };
  } catch (error) {
    console.error('Error fetching users:', error);
    return { items: [], summary: { total: 0, active: 0, suspended: 0, newToday: 0 } };
  }
};

export const updateUserStatus = async (id: string, isSuspended: boolean, reason?: string): Promise<any> => {
  try {
    const response = await API.patch(`/admin/users/${id}/status`, { isSuspended, reason });
    return response.data;
  } catch (error) {
    console.error('Error updating user status:', error);
    throw error;
  }
};

export const getUserProfile = async (id: string): Promise<any> => {
  try {
    const response = await API.get(`/admin/users/${id}/profile`);
    if (response.data.success) return response.data.data;
    return null;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
};

export const getScholarProfile = async (id: string): Promise<any> => {
  try {
    const response = await API.get(`/admin/scholars/${id}/profile`);
    if (response.data.success) return response.data.data;
    return null;
  } catch (error) {
    console.error('Error fetching scholar profile:', error);
    return null;
  }
};

export const suspendScholar = async (id: string, reason?: string) => {
  const response = await API.post(`/admin/scholars/${id}/suspend`, { reason });
  return response.data;
};

export const unsuspendScholar = async (id: string) => {
  const response = await API.post(`/admin/scholars/${id}/unsuspend`);
  return response.data;
};

/** Reject a specific document type for a scholar.
 *  docType: 'identity' | 'certification'
 *  This resets the scholar's onboarding step back to that document's upload step.
 */
export const rejectScholarDocument = async (
  scholarId: string,
  docType: 'identity' | 'certification',
  reason: string,
): Promise<void> => {
  const response = await API.post(`/admin/scholars/${scholarId}/reject-document`, { docType, reason });
  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to reject document');
  }
};

// ═══════════════════════════════════════════════════════════
// RECENT ACTIVITY & REVENUE CHART
// ═══════════════════════════════════════════════════════════

export const getRecentActivity = async (): Promise<any[]> => {
  try {
    const response = await API.get('/admin/recent-activity');
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) {
    console.error('Error fetching recent activity:', error);
    return [];
  }
};

export const getRevenueChart = async (period = 'month'): Promise<any[]> => {
  try {
    const response = await API.get(`/admin/revenue-chart?period=${period}`);
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) {
    console.error('Error fetching revenue chart:', error);
    return [];
  }
};

// ═══════════════════════════════════════════════════════════
// FINANCE & PAYOUTS
// ═══════════════════════════════════════════════════════════

export interface TransactionListResult {
  items: any[];
  summary: { total: number; successful: number; pending: number; failed: number; totalRevenue: number };
}

export const getTransactions = async (
  filters: { search?: string; type?: string } = {}
): Promise<TransactionListResult> => {
  try {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.type)   params.set('type',   filters.type);
    const qs = params.toString();
    const response = await API.get(`/admin/transactions${qs ? `?${qs}` : ''}`);
    if (response.data.success) return response.data.data;
    return { items: [], summary: { total: 0, successful: 0, pending: 0, failed: 0, totalRevenue: 0 } };
  } catch (error) {
    console.error('Error fetching transactions:', error);
    return { items: [], summary: { total: 0, successful: 0, pending: 0, failed: 0, totalRevenue: 0 } };
  }
};

export const getPayouts = async (filters?: { type?: string; status?: string }): Promise<any[]> => {
  try {
    const params = new URLSearchParams();
    if (filters?.type) params.set('type', filters.type);
    if (filters?.status) params.set('status', filters.status);
    const qs = params.toString();
    const response = await API.get(`/admin/payouts${qs ? `?${qs}` : ''}`);
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) {
    console.error('Error fetching payouts:', error);
    return [];
  }
};

export const processPayout = async (id: string): Promise<any> => {
  try {
    const response = await API.post(`/admin/payouts/${id}/process`);
    return response.data;
  } catch (error) {
    console.error('Error processing payout:', error);
    throw error;
  }
};

export const approvePayoutRequest = async (id: string, amount?: number): Promise<any> => {
  try {
    const response = await API.post(`/admin/payouts/${id}/approve`, amount !== undefined ? { amount } : {});
    return response.data;
  } catch (error) {
    console.error('Error approving payout:', error);
    throw error;
  }
};

export const updatePayoutAmount = async (id: string, amount: number): Promise<any> => {
  try {
    const response = await API.patch(`/admin/payouts/${id}/amount`, { amount });
    return response.data;
  } catch (error) {
    console.error('Error updating payout amount:', error);
    throw error;
  }
};

export const rejectPayoutRequest = async (id: string, reason: string): Promise<any> => {
  try {
    const response = await API.post(`/admin/payouts/${id}/reject`, { reason });
    return response.data;
  } catch (error) {
    console.error('Error rejecting payout:', error);
    throw error;
  }
};

export const getScholarPayoutSummary = async (scholarId: string): Promise<any> => {
  try {
    const response = await API.get(`/admin/payouts/scholar/${scholarId}/summary`);
    if (response.data.success) return response.data.data;
    return null;
  } catch (error) {
    console.error('Error fetching scholar earnings:', error);
    return null;
  }
};

export const getScholarPenalties = async (scholarId: string): Promise<any[]> => {
  try {
    const response = await API.get(`/admin/scholars/${scholarId}/penalties`);
    if (response.data.success) return response.data.data || [];
    return [];
  } catch (error) {
    console.error('Error fetching scholar penalties:', error);
    return [];
  }
};

export const getAllPenalties = async (): Promise<any[]> => {
  try {
    const response = await API.get('/admin/penalties');
    if (response.data.success) return response.data.data || [];
    return [];
  } catch (error) {
    console.error('Error fetching all penalties:', error);
    return [];
  }
};

export const waiveScholarPenalty = async (penaltyId: string, reason: string): Promise<any> => {
  try {
    const response = await API.post(`/admin/penalties/${penaltyId}/waive`, { reason });
    return response.data;
  } catch (error) {
    console.error('Error waiving scholar penalty:', error);
    throw error;
  }
};

export const getCancellationPolicyConfig = async (): Promise<Record<string, string> | null> => {
  try {
    const response = await API.get('/admin/cancellation-policy/config');
    if (response.data.success) return response.data.data;
    return null;
  } catch (error) {
    console.error('Error fetching cancellation policy config:', error);
    return null;
  }
};

export const updateCancellationPolicyConfig = async (config: Record<string, string>): Promise<any> => {
  try {
    const response = await API.put('/admin/cancellation-policy/config', config);
    return response.data;
  } catch (error) {
    console.error('Error updating cancellation policy config:', error);
    throw error;
  }
};

// ═══════════════════════════════════════════════════════════
// REIMBURSEMENTS
// ═══════════════════════════════════════════════════════════

export const createReimbursement = async (data: { bookingId: string; percentage: number; note?: string }): Promise<any> => {
  try {
    const response = await API.post('/admin/reimbursements', data);
    return response.data;
  } catch (error) {
    console.error('Error creating reimbursement:', error);
    throw error;
  }
};

export const getReimbursements = async (): Promise<any[]> => {
  try {
    const response = await API.get('/admin/reimbursements');
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) {
    console.error('Error fetching reimbursements:', error);
    return [];
  }
};

// ═══════════════════════════════════════════════════════════
// REFUNDS
// ═══════════════════════════════════════════════════════════

export const initiateRefund = async (data: { bookingId: string; refundType: string; customPercentage?: number; reason?: string }): Promise<any> => {
  try {
    const response = await API.post('/admin/refunds', data);
    return response.data;
  } catch (error) {
    console.error('Error initiating refund:', error);
    throw error;
  }
};

export const getRefunds = async (filters?: { status?: string }): Promise<any[]> => {
  try {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    const qs = params.toString();
    const response = await API.get(`/admin/refunds${qs ? `?${qs}` : ''}`);
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) {
    console.error('Error fetching refunds:', error);
    return [];
  }
};


// ═══════════════════════════════════════════════════════════
// SUPPORT & DISPUTES
// ═══════════════════════════════════════════════════════════

export interface SupportListResult {
  items: SupportTicket[];
  summary: { total: number; open: number; resolved: number; highPriority: number };
}

export interface SupportTicket {
  id: string;
  subject: string;
  description?: string;
  category?: string;
  priority: 'HIGH' | 'MEDIUM' | 'LOW' | string;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED' | string;
  assignedTeam?: 'SUPPORT' | 'OPERATIONS' | 'TECH_SUPPORT' | 'FINANCE' | string;
  createdAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
  resolutionMessage?: string;
  religion?: string;
  user?: { name?: string; email?: string; religion?: string };
  scholar?: { religion?: string; user?: { name?: string; email?: string } };
  assignedToAdminId?: string | null;
  assignedToAdmin?: { id: string; name: string; email?: string; religion?: string } | null;
}

export const getSupportTickets = async (
  filters: { search?: string; status?: string; priority?: string; team?: string } = {}
): Promise<SupportListResult> => {
  try {
    const params = new URLSearchParams();
    if (filters.search)   params.set('search',   filters.search);
    if (filters.status)   params.set('status',   filters.status);
    if (filters.priority) params.set('priority', filters.priority);
    if (filters.team)     params.set('team',     filters.team);
    const qs = params.toString();
    const response = await API.get(`/admin/support${qs ? `?${qs}` : ''}`);
    if (response.data.success) return response.data.data;
    return { items: [], summary: { total: 0, open: 0, resolved: 0, highPriority: 0 } };
  } catch (error) {
    console.error('Error fetching support tickets:', error);
    return { items: [], summary: { total: 0, open: 0, resolved: 0, highPriority: 0 } };
  }
};

export const updateTicketStatus = async (id: string, status: string, resolutionMessage?: string, resolvedBy?: string): Promise<any> => {
  try {
    const payload: any = { status };
    if (resolutionMessage) payload.resolutionMessage = resolutionMessage;
    if (resolvedBy) payload.resolvedBy = resolvedBy;
    const response = await API.patch(`/admin/support/${id}/status`, payload);
    return response.data;
  } catch (error) {
    console.error('Error updating ticket status:', error);
    throw error;
  }
};

export const reassignSupportTicket = async (id: string, assignedToAdminId: string): Promise<any> => {
  try {
    const response = await API.patch(`/admin/support/${id}/assign`, { assignedToAdminId });
    return response.data;
  } catch (error) {
    console.error('Error reassigning support ticket:', error);
    throw error;
  }
};

export const transferSupportTicketTeam = async (
  id: string,
  targetTeam: string,
  note?: string,
  assignedToId?: string
): Promise<any> => {
  try {
    const response = await API.post(`/admin/support/${id}/transfer-team`, {
      targetTeam,
      note,
      assignedToId,
    });
    return response.data;
  } catch (error) {
    console.error('Error transferring support ticket team:', error);
    throw error;
  }
};

export const getDepartments = async (): Promise<string[]> => {
  try {
    const response = await API.get('/admin/departments');
    if (response.data.success) return response.data.data;
    return ['SUPPORT', 'OPERATIONS', 'FINANCE', 'MARKETING'];
  } catch (error) {
    console.error('Error fetching departments:', error);
    return ['SUPPORT', 'OPERATIONS', 'FINANCE', 'MARKETING'];
  }
};

export const getTicketActivities = async (ticketId: string): Promise<any[]> => {
  try {
    const response = await API.get(`/admin/support/${ticketId}/activities`);
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) {
    console.error('Error fetching ticket activities:', error);
    return [];
  }
};

export const autoAssignTickets = async (): Promise<any> => {
  try {
    const response = await API.post('/admin/support/auto-assign');
    return response.data;
  } catch (error) {
    console.error('Error auto-assigning tickets:', error);
    throw error;
  }
};

export const getSupportStats = async (): Promise<any> => {
  try {
    const response = await API.get('/admin/support/stats');
    if (response.data.success) return response.data.data;
    return null;
  } catch (error) {
    console.error('Error fetching support stats:', error);
    return null;
  }
};

export const escalateTicket = async (id: string): Promise<any> => {
  try {
    const response = await API.post(`/admin/support/${id}/escalate`);
    return response.data;
  } catch (error) {
    console.error('Error escalating ticket:', error);
    throw error;
  }
};

export interface DisputeListResult {
  items: any[];
  summary: { total: number; open: number; resolved: number };
}

export interface BookingDisputeListResult {
  items: any[];
  summary: { total: number; totalAmount: number };
}

export const getDisputes = async (
  filters: { search?: string; status?: string } = {}
): Promise<DisputeListResult> => {
  try {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.status) params.set('status', filters.status);
    const qs = params.toString();
    const response = await API.get(`/admin/disputes${qs ? `?${qs}` : ''}`);
    if (response.data.success) return response.data.data;
    return { items: [], summary: { total: 0, open: 0, resolved: 0 } };
  } catch (error) {
    console.error('Error fetching disputes:', error);
    return { items: [], summary: { total: 0, open: 0, resolved: 0 } };
  }
};

export const createDispute = async (data: any): Promise<any> => {
  try {
    const response = await API.post('/admin/disputes', data);
    return response.data;
  } catch (error) {
    console.error('Error creating dispute:', error);
    throw error;
  }
};

export const resolveDispute = async (id: string, status: string, resolutionMessage?: string): Promise<any> => {
  try {
    const response = await API.patch(`/admin/disputes/${id}/resolve`, { status, resolutionMessage });
    return response.data;
  } catch (error) {
    console.error('Error resolving dispute:', error);
    throw error;
  }
};

export const getBookingDisputes = async (
  filters: { search?: string } = {}
): Promise<BookingDisputeListResult> => {
  try {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    const qs = params.toString();
    const response = await API.get(`/admin/bookings/disputed${qs ? `?${qs}` : ''}`);
    if (response.data.success) return response.data.data;
    return { items: [], summary: { total: 0, totalAmount: 0 } };
  } catch (error) {
    console.error('Error fetching booking disputes:', error);
    return { items: [], summary: { total: 0, totalAmount: 0 } };
  }
};

export const autoAssignDisputes = async (): Promise<any> => {
  try {
    const response = await API.post('/admin/disputes/auto-assign');
    return response.data;
  } catch (error) {
    console.error('Error auto-assigning disputes:', error);
    throw error;
  }
};

export const reassignDispute = async (id: string, assignedToAdminId: string): Promise<any> => {
  try {
    const response = await API.patch(`/admin/disputes/${id}/assign`, { assignedToAdminId });
    return response.data;
  } catch (error) {
    console.error('Error reassigning dispute:', error);
    throw error;
  }
};

export const getReligions = async (): Promise<string[]> => {
  try {
    const response = await API.get('/admin/religions');
    if (response.data.success) return response.data.data || [];
    return [];
  } catch (error) {
    console.error('Error fetching religions:', error);
    return [];
  }
};

/** Returns all religions registered by users/scholars on the platform (wider than getReligions which is admin-pool only). */
export const getPlatformReligions = async (): Promise<string[]> => {
  try {
    const response = await API.get('/admin/platform-religions');
    if (response.data.success) return response.data.data || [];
    return [];
  } catch (error) {
    console.error('Error fetching platform religions:', error);
    return [];
  }
};

// ═══════════════════════════════════════════════════════════
// ADVERTISEMENTS
// ═══════════════════════════════════════════════════════════

export const getAdvertisements = async (
  filters: { search?: string; status?: string; religion?: string } = {}
): Promise<any[]> => {
  try {
    const params = new URLSearchParams();
    if (filters.search)   params.set('search',   filters.search);
    if (filters.status)   params.set('status',   filters.status);
    if (filters.religion) params.set('religion', filters.religion);
    const qs = params.toString();
    const response = await API.get(`/admin/advertisements${qs ? `?${qs}` : ''}`);
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) {
    console.error('Error fetching advertisements:', error);
    return [];
  }
};

export const createAdvertisement = async (data: any): Promise<any> => {
  try {
    const response = await API.post('/admin/advertisements', data);
    return response.data;
  } catch (error) {
    console.error('Error creating advertisement:', error);
    throw error;
  }
};

export const updateAdvertisement = async (id: string, data: any): Promise<any> => {
  try {
    const response = await API.put(`/admin/advertisements/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating advertisement:', error);
    throw error;
  }
};

export const updateAdStatus = async (id: string, status: string): Promise<any> => {
  try {
    const response = await API.patch(`/admin/advertisements/${id}/status`, { status });
    return response.data;
  } catch (error) {
    console.error('Error updating ad status:', error);
    throw error;
  }
};

export const deleteAdvertisement = async (id: string): Promise<any> => {
  try {
    const response = await API.delete(`/admin/advertisements/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting advertisement:', error);
    throw error;
  }
};

export const getAdStats = async (): Promise<any> => {
  try {
    const response = await API.get('/admin/advertisements/stats');
    if (response.data.success) return response.data.data;
    return null;
  } catch (error) {
    console.error('Error fetching ad stats:', error);
    return null;
  }
};

// ═══════════════════════════════════════════════════════════
// PLATFORM CONFIG
// ═══════════════════════════════════════════════════════════

export const getConfig = async (category?: string): Promise<any[]> => {
  try {
    const url = category ? `/admin/config?category=${category}` : '/admin/config';
    const response = await API.get(url);
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) {
    console.error('Error fetching config:', error);
    return [];
  }
};

export const updateConfig = async (configs: { key: string; value: string; category?: string }[]): Promise<any> => {
  try {
    const response = await API.put('/admin/config', { configs });
    return response.data;
  } catch (error) {
    console.error('Error updating config:', error);
    throw error;
  }
};

export const exportConfig = async (): Promise<any> => {
  try {
    const response = await API.post('/admin/config/backup');
    return response.data;
  } catch (error) {
    console.error('Error exporting config:', error);
    throw error;
  }
};

// ═══════════════════════════════════════════════════════════
// REPORTS
// ═══════════════════════════════════════════════════════════

export const getReports = async (type?: string): Promise<any[]> => {
  try {
    const url = type ? `/admin/reports?type=${type}` : '/admin/reports';
    const response = await API.get(url);
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) {
    console.error('Error fetching reports:', error);
    return [];
  }
};

export const generateReport = async (data: { name: string; type: string; format?: string; filters?: any }): Promise<any> => {
  try {
    const response = await API.post('/admin/reports/generate', data);
    return response.data;
  } catch (error) {
    console.error('Error generating report:', error);
    throw error;
  }
};

export const getSignedReportUrl = async (id: string): Promise<string | null> => {
  try {
    const response = await API.get(`/admin/reports/${id}/download`);
    if (response.data.success) return response.data.data.signedUrl;
    return null;
  } catch (error) {
    console.error('Error fetching signed report url:', error);
    return null;
  }
};

export const exportRawDatabase = async (collection: string): Promise<Blob | null> => {
  try {
    const response = await API.get(`/admin/reports/export-raw?collection=${collection}`, {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting raw database:', error);
    return null;
  }
};

export const scheduleReport = async (data: { name: string; type: string; schedule: string; format?: string }): Promise<any> => {
  try {
    const response = await API.post('/admin/reports/schedule', data);
    return response.data;
  } catch (error) {
    console.error('Error scheduling report:', error);
    throw error;
  }
};

export const updateReportSchedule = async (id: string, schedule: string): Promise<any> => {
  try {
    const response = await API.patch(`/admin/reports/${id}/schedule`, { schedule });
    return response.data;
  } catch (error) {
    console.error('Error updating report schedule:', error);
    throw error;
  }
};

export const deleteReport = async (id: string): Promise<any> => {
  try {
    const response = await API.delete(`/admin/reports/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting report:', error);
    throw error;
  }
};

export const getReportCategories = async (): Promise<any[]> => {
  try {
    const response = await API.get('/admin/reports/categories');
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) {
    console.error('Error fetching report categories:', error);
    return [];
  }
};

// ═══════════════════════════════════════════════════════════
// ADMINS & ORG REPORTS
// ═══════════════════════════════════════════════════════════

export const getAdmins = async (): Promise<AdminListItem[]> => {
  try {
    const response = await API.get('/admin/admins');
    if (response.data.success) return response.data.data || [];
    return [];
  } catch (error) {
    console.error('Error fetching admins:', error);
    return [];
  }
};

export const getAdminHierarchy = async (): Promise<{ tree: HierarchyNode[], mappings: any[] }> => {
  try {
    const response = await API.get('/admin/hierarchy');
    if (response.data.success) return response.data.data || { tree: [], mappings: [] };
    return { tree: [], mappings: [] };
  } catch (error) {
    console.error('Error fetching admin hierarchy:', error);
    return { tree: [], mappings: [] };
  }
};

export const getAdminPerformanceReport = async (): Promise<AdminPerformanceItem[]> => {
  try {
    const response = await API.get('/admin/reports/admin-performance');
    if (response.data.success) return response.data.data || [];
    return [];
  } catch (error) {
    console.error('Error fetching admin performance report:', error);
    return [];
  }
};

// ═══════════════════════════════════════════════════════════
// AUTOMATION
// ═══════════════════════════════════════════════════════════

export interface AutomationListResult {
  items: any[];
  summary: { totalRules: number; activeRules: number; inactiveRules: number };
}

export const getAutomationRules = async (
  filters: { search?: string; status?: string } = {}
): Promise<AutomationListResult> => {
  try {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.status) params.set('status', filters.status);
    const qs = params.toString();
    const response = await API.get(`/admin/automation${qs ? `?${qs}` : ''}`);
    if (response.data.success) return response.data.data;
    return { items: [], summary: { totalRules: 0, activeRules: 0, inactiveRules: 0 } };
  } catch (error) {
    console.error('Error fetching automation rules:', error);
    return { items: [], summary: { totalRules: 0, activeRules: 0, inactiveRules: 0 } };
  }
};

export const createAutomationRule = async (data: any): Promise<any> => {
  try {
    const response = await API.post('/admin/automation', data);
    return response.data;
  } catch (error) {
    console.error('Error creating automation rule:', error);
    throw error;
  }
};

export const updateAutomationRule = async (id: string, data: any): Promise<any> => {
  try {
    const response = await API.put(`/admin/automation/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating automation rule:', error);
    throw error;
  }
};

export const updateRuleStatus = async (id: string, status: string): Promise<any> => {
  try {
    const response = await API.patch(`/admin/automation/${id}/status`, { status });
    return response.data;
  } catch (error) {
    console.error('Error updating rule status:', error);
    throw error;
  }
};

export const deleteAutomationRule = async (id: string): Promise<any> => {
  try {
    const response = await API.delete(`/admin/automation/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting automation rule:', error);
    throw error;
  }
};

export const getAutomationStats = async (): Promise<any> => {
  try {
    const response = await API.get('/admin/automation/stats');
    if (response.data.success) return response.data.data;
    return null;
  } catch (error) {
    console.error('Error fetching automation stats:', error);
    return null;
  }
};

export const getAutomationTemplates = async (): Promise<any[]> => {
  try {
    const response = await API.get('/admin/automation/templates');
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) {
    console.error('Error fetching automation templates:', error);
    return [];
  }
};

export const runAutomationRule = async (id: string): Promise<any> => {
  try {
    const response = await API.post(`/admin/automation/${id}/run`);
    return response.data;
  } catch (error) {
    console.error('Error running automation rule:', error);
    throw error;
  }
};

// ═══════════════════════════════════════════════════════════
// AUDIT LOGS
// ═══════════════════════════════════════════════════════════

export const getAuditLogs = async (filters: { 
  search?: string; 
  entity?: string; 
  action?: string; 
  adminId?: string;
  take?: number;
  skip?: number;
} = {}): Promise<any[]> => {
  try {
    const params = new URLSearchParams();
    if (filters.search)  params.set('search',  filters.search);
    if (filters.entity)  params.set('entity',  filters.entity);
    if (filters.action)  params.set('action',  filters.action);
    if (filters.adminId) params.set('adminId', filters.adminId);
    if (filters.take)    params.set('take',    String(filters.take));
    if (filters.skip)    params.set('skip',    String(filters.skip));
    
    const qs = params.toString();
    const response = await API.get(`/admin/logs${qs ? `?${qs}` : ''}`);
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) {
    console.error('Error fetching audit logs:', error);
    return [];
  }
};

// ═══════════════════════════════════════════════════════════
// COMMUNITY CONFIGS
// ═══════════════════════════════════════════════════════════

export const getCommunityConfigs = async (religion?: string): Promise<any[]> => {
  try {
    const url = religion ? `/admin/community-configs?religion=${religion}` : '/admin/community-configs';
    const response = await API.get(url);
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) { console.error('Error fetching community configs:', error); return []; }
};

export const createCommunityConfig = async (data: any): Promise<any> => {
  const response = await API.post('/admin/community-configs', data);
  return response.data;
};

export const updateCommunityConfig = async (id: string, data: any): Promise<any> => {
  const response = await API.put(`/admin/community-configs/${id}`, data);
  return response.data;
};

export const deleteCommunityConfig = async (id: string): Promise<any> => {
  const response = await API.delete(`/admin/community-configs/${id}`);
  return response.data;
};

// ═══════════════════════════════════════════════════════════
// EXPERIENCE LEVELS
// ═══════════════════════════════════════════════════════════

export const getExperienceLevels = async (): Promise<any[]> => {
  try {
    const response = await API.get('/admin/experience-levels');
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) { console.error('Error fetching experience levels:', error); return []; }
};

export const createExperienceLevel = async (data: any): Promise<any> => {
  const response = await API.post('/admin/experience-levels', data);
  return response.data;
};

export const updateExperienceLevel = async (id: string, data: any): Promise<any> => {
  const response = await API.put(`/admin/experience-levels/${id}`, data);
  return response.data;
};

export const deleteExperienceLevel = async (id: string): Promise<any> => {
  const response = await API.delete(`/admin/experience-levels/${id}`);
  return response.data;
};

// ═══════════════════════════════════════════════════════════
// GOVERNMENT ID TYPES
// ═══════════════════════════════════════════════════════════

export const getGovernmentIdTypes = async (): Promise<any[]> => {
  try {
    const response = await API.get('/admin/government-id-types');
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) { console.error('Error fetching government ID types:', error); return []; }
};

export const createGovernmentIdType = async (data: any): Promise<any> => {
  const response = await API.post('/admin/government-id-types', data);
  return response.data;
};

export const updateGovernmentIdType = async (id: string, data: any): Promise<any> => {
  const response = await API.put(`/admin/government-id-types/${id}`, data);
  return response.data;
};

export const deleteGovernmentIdType = async (id: string): Promise<any> => {
  const response = await API.delete(`/admin/government-id-types/${id}`);
  return response.data;
};

// ═══════════════════════════════════════════════════════════
// SCHOLAR TYPE CONFIGS
// ═══════════════════════════════════════════════════════════

export const getScholarTypeConfigs = async (religion?: string): Promise<any[]> => {
  try {
    const url = religion ? `/admin/scholar-type-configs?religion=${religion}` : '/admin/scholar-type-configs';
    const response = await API.get(url);
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) { console.error('Error fetching scholar type configs:', error); return []; }
};

export const createScholarTypeConfig = async (data: any): Promise<any> => {
  const response = await API.post('/admin/scholar-type-configs', data);
  return response.data;
};

export const updateScholarTypeConfig = async (id: string, data: any): Promise<any> => {
  const response = await API.put(`/admin/scholar-type-configs/${id}`, data);
  return response.data;
};

export const deleteScholarTypeConfig = async (id: string): Promise<any> => {
  const response = await API.delete(`/admin/scholar-type-configs/${id}`);
  return response.data;
};

// ═══════════════════════════════════════════════════════════
// GUIDE SECTIONS
// ═══════════════════════════════════════════════════════════

export const getGuideSections = async (): Promise<any[]> => {
  try {
    const response = await API.get('/admin/guide-sections');
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) { console.error('Error fetching guide sections:', error); return []; }
};

export const createGuideSection = async (data: any): Promise<any> => {
  const response = await API.post('/admin/guide-sections', data);
  return response.data;
};

export const updateGuideSection = async (id: string, data: any): Promise<any> => {
  const response = await API.put(`/admin/guide-sections/${id}`, data);
  return response.data;
};

export const deleteGuideSection = async (id: string): Promise<any> => {
  const response = await API.delete(`/admin/guide-sections/${id}`);
  return response.data;
};

// ═══════════════════════════════════════════════════════════
// LEGAL DOCUMENTS
// ═══════════════════════════════════════════════════════════

export const getLegalDocuments = async (targetApp?: string): Promise<any[]> => {
  try {
    const url = targetApp ? `/admin/legal-documents?targetApp=${targetApp}` : '/admin/legal-documents';
    const response = await API.get(url);
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) { console.error('Error fetching legal documents:', error); return []; }
};

export const createLegalDocument = async (data: any): Promise<any> => {
  const response = await API.post('/admin/legal-documents', data);
  return response.data;
};

export const updateLegalDocument = async (id: string, data: any): Promise<any> => {
  const response = await API.put(`/admin/legal-documents/${id}`, data);
  return response.data;
};

export const deleteLegalDocument = async (id: string): Promise<any> => {
  const response = await API.delete(`/admin/legal-documents/${id}`);
  return response.data;
};

// ═══════════════════════════════════════════════════════════
// RELIGION STATE LANGUAGES
// ═══════════════════════════════════════════════════════════

export const getReligionStateLanguages = async (religion?: string): Promise<any[]> => {
  try {
    const url = religion ? `/admin/religion-state-languages?religion=${religion}` : '/admin/religion-state-languages';
    const response = await API.get(url);
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) { console.error('Error fetching religion state languages:', error); return []; }
};

export const createReligionStateLanguage = async (data: any): Promise<any> => {
  const response = await API.post('/admin/religion-state-languages', data);
  return response.data;
};

export const updateReligionStateLanguage = async (id: string, data: any): Promise<any> => {
  const response = await API.put(`/admin/religion-state-languages/${id}`, data);
  return response.data;
};

export const deleteReligionStateLanguage = async (id: string): Promise<any> => {
  const response = await API.delete(`/admin/religion-state-languages/${id}`);
  return response.data;
};

// ═══════════════════════════════════════════════════════════
// RELIGIOUS REFERENCE TYPES
// ═══════════════════════════════════════════════════════════

export const getReligiousReferenceTypes = async (religion?: string): Promise<any[]> => {
  try {
    const url = religion ? `/admin/religious-reference-types?religion=${religion}` : '/admin/religious-reference-types';
    const response = await API.get(url);
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) { console.error('Error fetching religious reference types:', error); return []; }
};

export const createReligiousReferenceType = async (data: any): Promise<any> => {
  const response = await API.post('/admin/religious-reference-types', data);
  return response.data;
};

export const updateReligiousReferenceType = async (id: string, data: any): Promise<any> => {
  const response = await API.put(`/admin/religious-reference-types/${id}`, data);
  return response.data;
};

export const deleteReligiousReferenceType = async (id: string): Promise<any> => {
  const response = await API.delete(`/admin/religious-reference-types/${id}`);
  return response.data;
};

// ═══════════════════════════════════════════════════════════
// OPERATIONAL CITIES
// ═══════════════════════════════════════════════════════════

export const getOperationalCities = async (): Promise<any[]> => {
  try {
    const response = await API.get('/admin/operational-cities');
    if (response.data.success) return response.data.data;
    return [];
  } catch (error) { console.error('Error fetching operational cities:', error); return []; }
};

export const createOperationalCity = async (data: any): Promise<any> => {
  const response = await API.post('/admin/operational-cities', data);
  return response.data;
};

export const updateOperationalCity = async (id: string, data: any): Promise<any> => {
  const response = await API.put(`/admin/operational-cities/${id}`, data);
  return response.data;
};

export const deleteOperationalCity = async (id: string): Promise<any> => {
  const response = await API.delete(`/admin/operational-cities/${id}`);
  return response.data;
};

// ═══════════════════════════════════════════════════════════
// ABOUT APPLICATION
// ═══════════════════════════════════════════════════════════

export const getAboutInfo = async (targetApp?: string): Promise<any> => {
  try {
    const url = targetApp ? `/about?targetApp=${targetApp}` : '/about';
    const response = await API.get(url);
    if (response.data.success) return response.data.data;
    return null;
  } catch (error) { console.error('Error fetching about info:', error); return null; }
};

export const updateAboutInfo = async (data: any): Promise<any> => {
  const response = await API.post('/about', data);
  return response.data;
};

export const updateAdminProfile = async (data: { availabilityStatus?: string; supportAvatarUrl?: string; name?: string }): Promise<any> => {
  const response = await API.put('/auth/profile', data);
  return response.data;
};

export const getMe = async (): Promise<any> => {
  const response = await API.get('/auth/me');
  return response.data;
};

// ═══════════════════════════════════════════════════════════
// PUSH CAMPAIGNS & RETENTION
// ═══════════════════════════════════════════════════════════

export interface PushCampaignItem {
  id: string;
  title: string;
  body: string;
  imageUrl?: string | null;
  targetAudience: string;
  targetFilters?: any;
  deepLink?: string | null;
  type: string;
  scheduledAt?: string | null;
  sentAt?: string | null;
  status: 'DRAFT' | 'SCHEDULED' | 'SENDING' | 'SENT' | 'FAILED' | 'CANCELLED';
  totalTargeted: number;
  successCount: number;
  failureCount: number;
  createdBy?: { id: string; name: string; email: string; role: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface PushAnalyticsData {
  activeTokens: {
    customers: number;
    scholars: number;
    total: number;
  };
  campaigns: {
    total: number;
    sent: number;
    scheduled: number;
    totalDelivered: number;
    totalTargeted: number;
    deliveryRate: string;
  };
}

export const getPushCampaignAnalytics = async (): Promise<PushAnalyticsData> => {
  const res = await API.get('/admin/notifications/analytics');
  return res.data.data;
};

export const getAudienceReachCount = async (targetAudience: string, filters?: any): Promise<{
  totalUsers: number;
  fcmTokenCount: number;
  expoTokenCount: number;
  reachableUsers: number;
}> => {
  const params: any = { targetAudience };
  if (filters) {
    if (filters.city) params.city = filters.city;
    if (filters.state) params.state = filters.state;
    if (filters.religion) params.religion = filters.religion;
    if (filters.role) params.role = filters.role;
  }
  const res = await API.get('/admin/notifications/audience-count', { params });
  return res.data.data;
};

export const getPushCampaigns = async (params?: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}): Promise<{ items: PushCampaignItem[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> => {
  const res = await API.get('/admin/notifications/campaigns', { params });
  return res.data.data;
};

export const getPushCampaignById = async (id: string): Promise<PushCampaignItem> => {
  const res = await API.get(`/admin/notifications/campaigns/${id}`);
  return res.data.data;
};

export const createPushCampaign = async (payload: {
  title: string;
  body: string;
  imageUrl?: string;
  targetAudience: string;
  targetFilters?: any;
  deepLink?: string;
  type?: string;
  scheduledAt?: string | null;
}): Promise<PushCampaignItem> => {
  const res = await API.post('/admin/notifications/broadcast', payload);
  return res.data.data;
};

export const cancelPushCampaign = async (id: string): Promise<any> => {
  const res = await API.post(`/admin/notifications/campaigns/${id}/cancel`);
  return res.data.data;
};

export const deletePushCampaign = async (id: string): Promise<any> => {
  const res = await API.delete(`/admin/notifications/campaigns/${id}`);
  return res.data.data;
};

